// BANNIÈRE DE MISE À JOUR — affichée EN HAUT de l'écran quand une nouvelle version est en ligne.
//
// ═══ LE BUG QU'ELLE CORRIGE (12/07/2026) ═══
// L'application était configurée en registerType 'autoUpdate'. Concrètement, le service worker
// généré appelait skipWaiting() + clientsClaim() + cleanupOutdatedCaches() : la nouvelle version
// prenait le contrôle IMMÉDIATEMENT et purgeait l'ancien cache — MAIS SANS RECHARGER LA PAGE.
// Or l'application charge ses écrans À LA DEMANDE (MesStock, MesArticles… sont des fichiers
// séparés). La page, toujours sur l'ANCIEN code, réclamait donc les ANCIENS fichiers d'écran.
// Résultat : un écran affichait l'ancienne version, un autre la nouvelle, au gré des
// rechargements. C'est ce que Thérèse a vécu comme une « instabilité » : ce n'en était pas une,
// c'était mécanique et reproductible.
//
// ═══ LA CORRECTION ═══
// registerType passe à 'prompt' (vite.config.ts) : le nouveau service worker ATTEND au lieu de
// s'imposer. L'ancienne version continue donc de tourner de façon COHÉRENTE (ancien code ET
// anciens écrans), sans aucun mélange. Cette bannière prévient qu'une mise à jour existe ; au
// clic, updateServiceWorker() active le nouveau service worker ET recharge la page — tout bascule
// d'un bloc.
//
// ═══ POURQUOI PAS DE RECHARGEMENT AUTOMATIQUE ═══
// Recharger sans prévenir ferait perdre une saisie en cours (un devis à moitié rempli, par
// exemple). Thérèse décide du moment. C'est plus sûr, et surtout : elle SAIT qu'une version
// existe, au lieu de la subir.
//
// NB : les DONNÉES ne sont jamais concernées. Elles ne transitent par aucun cache (l'API est en
// NetworkOnly) et ne sont jamais stockées sur la machine — vérifié : aucun localStorage,
// sessionStorage ni indexedDB dans l'application.

import { useState } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'

/** Intervalle de vérification d'une nouvelle version (1 heure). */
const INTERVALLE_VERIFICATION_MS = 60 * 60 * 1000

// Délai maximal d'attente avant de recharger d'office (voir mettreAJour ci-dessous).
const DELAI_RECHARGEMENT_MS = 1500

export default function BanniereMaj() {
  const [enCours, setEnCours] = useState(false)
  const {
    needRefresh: [besoinMaj, setBesoinMaj],
    updateServiceWorker,
  } = useRegisterSW({
    // Vérifie au démarrage PUIS toutes les heures : une nouvelle version déployée est signalée
    // rapidement, sans attendre une fermeture complète de l'application.
    onRegisteredSW(_url, registration) {
      if (!registration) return
      void registration.update()
      setInterval(() => void registration.update(), INTERVALLE_VERIFICATION_MS)
    },
  })

  // ═══ POURQUOI ON RECHARGE NOUS-MÊMES (correctif du 13/07/2026) ═══
  // BUG CONSTATÉ : la bannière s'affichait bien, mais « Mettre à jour » ne faisait RIEN.
  // CAUSE, lue dans le code de vite-plugin-pwa (v1.3) :
  //   • en mode 'prompt', updateServiceWorker(reloadPage) IGNORE son paramètre : il se contente
  //     d'envoyer SKIP_WAITING au service worker, il ne recharge jamais la page ;
  //   • le rechargement est délégué à un écouteur interne :
  //         wb.addEventListener('controlling', (e) => { if (e.isUpdate) window.location.reload() })
  //     et `isUpdate` n'est VRAI que si la page était DÉJÀ contrôlée par un service worker au
  //     chargement. Après un rechargement forcé (Ctrl+Shift+R), la page démarre SANS contrôleur
  //     → `isUpdate` est faux → aucun rechargement → le bouton paraît mort.
  // CORRECTIF : on ne délègue plus. On écoute nous-mêmes `controllerchange` (le nouveau service
  // worker vient de prendre la main → on recharge), et on garde un FILET : si le contrôleur ne
  // change pas (cas d'une page non contrôlée), on recharge quand même après un court délai. Un
  // rechargement suffit : le premier qui part interrompt la page, l'autre n'a pas lieu.
  async function mettreAJour() {
    setEnCours(true)
    navigator.serviceWorker?.addEventListener(
      'controllerchange',
      () => window.location.reload(),
      { once: true },
    )
    try {
      await updateServiceWorker(true)
    } finally {
      // Filet de sécurité : la page repart même si aucun changement de contrôleur n'arrive.
      setTimeout(() => window.location.reload(), DELAI_RECHARGEMENT_MS)
    }
  }

  if (!besoinMaj) return null

  return (
    <div className="sticky top-0 z-50 border-b border-bdspIndigo/20 bg-bdspIndigo px-4 py-2 text-white shadow-sm print:hidden">
      <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium">
          ✨ Une nouvelle version de BD-SapoPro est disponible.
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void mettreAJour()}
            disabled={enCours}
            className="rounded-lg bg-white px-3 py-1 text-sm font-semibold text-bdspIndigo transition hover:bg-stone-100 disabled:opacity-60"
          >
            {enCours ? 'Mise à jour…' : 'Mettre à jour'}
          </button>
          <button
            type="button"
            onClick={() => setBesoinMaj(false)}
            className="rounded-lg border border-white/40 px-3 py-1 text-sm font-medium text-white transition hover:bg-white/10"
            aria-label="Masquer la bannière de mise à jour"
          >
            Plus tard
          </button>
        </div>
      </div>
    </div>
  )
}
